import { useEffect, useRef } from "react";

const SITE_URL = typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env
  ? (import.meta as Record<string, Record<string, string>>).env.VITE_SITE_URL || "https://cssbuyspreadsheet.delivery"
  : "https://cssbuyspreadsheet.delivery";

const BASE_PATH = typeof __BASE_PATH__ !== "undefined" ? String(__BASE_PATH__) : "";

function buildCanonical(path: string): string {
  const base = BASE_PATH.split("/").filter(Boolean).join("/");
  const prefix = base ? `/${base}` : "";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${prefix}${cleanPath}`;
}

interface SEOInput {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function useSEO(input: SEOInput): void {
  const {
    title,
    description,
    keywords,
    canonicalPath,
    ogImage,
    ogType = "website",
    jsonLd,
  } = input;

  const metaRefs = useRef<HTMLMetaElement[]>([]);
  const linkRef = useRef<HTMLLinkElement | null>(null);
  const ldRefs = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    // 1. Title
    const prevTitle = document.title;
    document.title = title;

    // 2. Description
    const descEl = createOrUpdateMeta("description", description);
    metaRefs.current.push(descEl);

    // 3. Keywords
    if (keywords) {
      const kwEl = createOrUpdateMeta("keywords", keywords);
      metaRefs.current.push(kwEl);
    }

    // 4. Canonical
    const canonicalUrl = buildCanonical(canonicalPath);
    let canonicalLink = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    linkRef.current = canonicalLink;

    // 5. OG tags
    const ogTitle = createOrUpdateMeta("og:title", title, true);
    const ogDesc = createOrUpdateMeta("og:description", description, true);
    const ogUrl = createOrUpdateMeta("og:url", canonicalUrl, true);
    const ogTypeEl = createOrUpdateMeta("og:type", ogType, true);
    metaRefs.current.push(ogTitle, ogDesc, ogUrl, ogTypeEl);

    if (ogImage) {
      const ogImg = createOrUpdateMeta("og:image", ogImage, true);
      metaRefs.current.push(ogImg);
    }

    // 6. Twitter tags
    const twCard = createOrUpdateMeta("twitter:card", "summary_large_image");
    const twTitle = createOrUpdateMeta("twitter:title", title);
    const twDesc = createOrUpdateMeta("twitter:description", description);
    metaRefs.current.push(twCard, twTitle, twDesc);
    if (ogImage) {
      const twImg = createOrUpdateMeta("twitter:image", ogImage);
      metaRefs.current.push(twImg);
    }

    // 7. JSON-LD
    if (jsonLd) {
      const ldArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      ldArray.forEach((ld) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
        ldRefs.current.push(script);
      });
    }

    // 8. Robots
    const robotsEl = createOrUpdateMeta("robots", "index, follow");
    metaRefs.current.push(robotsEl);

    return () => {
      // Cleanup injected elements
      document.title = prevTitle;
      metaRefs.current.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      metaRefs.current = [];

      if (linkRef.current && linkRef.current.parentNode) {
        linkRef.current.parentNode.removeChild(linkRef.current);
        linkRef.current = null;
      }

      ldRefs.current.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      ldRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonicalPath, ogImage, ogType]);
}

function createOrUpdateMeta(
  name: string,
  content: string,
  isProperty = false
): HTMLMetaElement {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}='${CSS.escape(name)}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
  return el;
}

// JSON-LD helpers
export function buildWebSiteSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CSSBuy Spreadsheet",
    url: SITE_URL,
    description:
      "Guia completo do CSSBuy Spreadsheet 2026. Aprenda a usar, comparar e economizar com o melhor agente de compras da China.",
    inLanguage: "pt-BR",
  };
}

export function buildOrganizationSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CSSBuy Spreadsheet",
    url: SITE_URL,
    description:
      "Site independente de guias e tutoriais sobre CSSBuy Spreadsheet. Não somos afiliados ao CSSBuy.",
    email: "contato@cssbuyspreadsheet.delivery",
  };
}

export function buildArticleSchema(
  headline: string,
  description: string,
  path: string,
  datePublished: string,
  dateModified: string,
  imageUrl?: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url: buildCanonical(path),
    datePublished,
    dateModified,
    author: {
      "@type": "Organization",
      name: "CSSBuy Spreadsheet",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "CSSBuy Spreadsheet",
      url: SITE_URL,
    },
    inLanguage: "pt-BR",
    ...(imageUrl ? { image: imageUrl } : {}),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": buildCanonical(path),
    },
  };
}

export function buildFAQSchema(
  faqs: { question: string; answer: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(
  items: { name: string; path: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: buildCanonical(item.path),
    })),
  };
}

export { buildCanonical, SITE_URL };